import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Button,
  Box,
  Typography,
  TextField,
  Tooltip,
  CircularProgress
} from '@material-ui/core';
import TemplateEditor from 'components/Template/TemplateEditor';
import VerifyTemplate from 'views/UserMessaging/customModalContent/verifyTemplate';
import SaveMessage from 'views/UserMessaging/customModalContent/saveMessage';
import SaveConfirm from 'views/UserMessaging/customModalContent/saveConfirmTemplate';
import { Alert } from '@material-ui/lab';
import { CloseIcon } from 'components';
import { getEnvVar } from 'helpers';
import { ProductType } from 'constants.js';

const useStyles = makeStyles(theme => ({
  root: {},
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between'
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    width: '95%'
  },
  autoSave: {
    marginLeft: '40%'
  },
  cardHeader: {},
  cardActions: {
    flexDirection: 'row'
  },
  inputGrid: {
    maxWidth: '40rem'
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    margin: '20%'
  }
}));

const Design = props => {
  const {
    className,
    staticContext,
    template,
    back,
    showNav,
    isPopupDialog,
    isCustomizeTemplate,
    editor,
    sendResponse,
    ...rest
  } = props;
  const {
    isLoading,
    shouldAutoSave,
    isAutoSaveSuccess,
    isTestingTemplate,
    isTestingSucceeded,
    isTemplateSaved,
    isTemplateSaving,
    goBack,
    navBackOnError,
    thisTemplate,
    handleChange,
    saveTemplate,
    onFocus,
    onBlur,
    closeTemplate,
    openTestTemplateModal,
    closeTestTemplateModal,
    shouldShowError,
    getErrorMessage,
    getTestTemplate,
    updateCurrentErrorValidationSegment,
    validateTestTemplate,
    startAutoSaveTimer,
    autoSaveTemplate,
    isTemplateReadOnly,
    showWarningMessage,
    isTenantDefaultTemplate
  } = template;

  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [isValidationError, setIsValidationError] = useState({});

  const classes = useStyles();
  const editorRef = editor || React.createRef();

  useEffect(() => {
    if (!isLoading) {
      startAutoSaveTimer();
    }
  }, [isLoading]);

  useEffect(() => {
    updateCurrentErrorValidationSegment('design');
    navBackOnError(back);
  }, [thisTemplate]);

  useEffect(() => {
    if (isTemplateSaved) {
      startAutoSaveTimer();
    }
  }, [isTemplateSaved]);

  useEffect(() => {
    if (shouldAutoSave && !isCustomizeTemplate) {
      editorRef.current.exportAndSaveTemplate({
        type: 'autoSaveTemplate'
      });
    }
  }, [shouldAutoSave]);

  const onClickSaveButton = () => {
    if (showWarningMessage()) {
      setShowSaveConfirmModal(true);
    } else {
      editorRef.current.exportAndSaveTemplate();
    }
  };

  const saveConfirmTemplate = () => {
    setShowSaveConfirmModal(false);
    editorRef.current.exportAndSaveTemplate();
  };

  const closeSaveConfirm = () => {
    setShowSaveConfirmModal(false);
  };

  const validateErrors = () => {
    let err = Object.values(isValidationError).filter(val => val.isDisabled);
    return err.length > 0 ? true : false;
  };

  return (
    <div className={classes.root}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form autoComplete="off">
          {!isPopupDialog && (
            <React.Fragment>
              <div>
                {isTemplateReadOnly(isPopupDialog) && (
                  <Alert severity="info">
                    You can only view this template and do not have access to
                    edit.{' '}
                    {isTenantDefaultTemplate()
                      ? "Tenant Default template can be edited only with 'ALL' property."
                      : 'Only admin can edit Global Default template.'}
                  </Alert>
                )}
              </div>
              <div className={classes.headerContainer}>
                <div className={classes.title}>
                  <CardHeader
                    subheader="* indicates a required field"
                    title="Survey Result Response Template Design"
                    titleTypographyProps={{ variant: 'h2' }}
                  />
                </div>
                <CloseIcon onClickClose={closeTemplate} />
              </div>
            </React.Fragment>
          )}
          <CardContent>
            {thisTemplate.type === 'EMAIL' &&
              thisTemplate.schemaIdentifier && (
                <TemplateEditor
                  useUnsubscribeLinkOption={false}
                  useSurveysLinkOption={false}
                  ref={editorRef}
                  thisTemplate={thisTemplate}
                  templateKeys={thisTemplate.eventMetaData}
                  handleChange={handleChange}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  saveTemplate={saveTemplate}
                  validateTestTemplate={validateTestTemplate}
                  shouldAutoSave={shouldAutoSave}
                  autoSaveTemplate={autoSaveTemplate}
                  isPopupDialog={isPopupDialog}
                  sendResponse={sendResponse}
                  isTemplateReadOnly={isTemplateReadOnly}
                  setIsValidationError={setIsValidationError}
                  bypassLanguageCheck={true}>
                  <TextField
                    fullWidth
                    disabled={isTemplateReadOnly(isPopupDialog)}
                    label="Email Subject Line"
                    margin="dense"
                    name="emailSubject"
                    onChange={handleChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    placeholder="Hello {{firstName}}"
                    required
                    value={thisTemplate.emailSubject || ''}
                    variant="outlined"
                    error={shouldShowError('emailSubject')}
                    helperText={getErrorMessage('emailSubject')}
                  />
                  {shouldAutoSave && (
                    <Typography
                      color="textSecondary"
                      variant="body2"
                      className={classes.autoSave}>
                      AutoSaving...
                    </Typography>
                  )}
                </TemplateEditor>
              )}
          </CardContent>
          {(back || showNav || isPopupDialog) && (
            <>
              {!isPopupDialog && <Divider />}
              <CardActions className={classes.cardActions}>
                {!isPopupDialog && (
                  <Button
                    color="secondary"
                    variant="contained"
                    disabled={isLoading}
                    link={back}
                    onClick={goBack}>
                    Back
                  </Button>
                )}
                <Typography></Typography>
                <Box flexGrow={1}>&nbsp;</Box>
                {(thisTemplate.productType !== ProductType.List ||
                  getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT_PHASE2') ===
                    'true') && (
                  <React.Fragment>
                    <VerifyTemplate
                      templateType={thisTemplate.type}
                      openModel={openTestTemplateModal}
                      closeModel={closeTestTemplateModal}
                      getTestTemplate={getTestTemplate}
                      isTestingTemplate={isTestingTemplate}
                      isTestingSucceeded={isTestingSucceeded}
                    />
                    <Button
                      color="primary"
                      variant="contained"
                      disabled={isLoading || isTemplateSaving}
                      onClick={() =>
                        editorRef.current.exportAndSaveTemplate({
                          type: 'testTemplate'
                        })
                      }>
                      Test Template
                    </Button>
                  </React.Fragment>
                )}
                {!isTemplateReadOnly(isPopupDialog) &&
                  !isCustomizeTemplate &&
                  (validateErrors() ? (
                    <Tooltip title="Fix the handlebar formats">
                      <span>
                        <Button
                          color="primary"
                          variant="contained"
                          disabled={() => validateErrors}
                          onClick={() => onClickSaveButton()}>
                          Save Template
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <Button
                      color="primary"
                      variant="contained"
                      disabled={isLoading || isTemplateSaving}
                      onClick={() => onClickSaveButton()}>
                      Save Template
                    </Button>
                  ))}
              </CardActions>
            </>
          )}
        </form>
      </Card>
      {!isAutoSaveSuccess && <SaveMessage openSnackbar={isTemplateSaved} />}
      {showSaveConfirmModal && (
        <SaveConfirm
          closeModel={closeSaveConfirm}
          saveConfirmTemplate={saveConfirmTemplate}></SaveConfirm>
      )}
    </div>
  );
};

export default Design;
